import { createContext, useContext, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, AlertTriangle, Info, X } from 'lucide-react';

/**
 * FE-5: a single toast provider at the app root, reached through useToast().
 * No component renders its own notification.
 */
const ToastContext = createContext(null);

// FE-6: success dismisses after 5s; errors persist to 8s and are dismissible,
// because a person needs time to read what went wrong.
const DURATIONS = { success: 5000, info: 5000, error: 8000 };

const ICONS = { success: Check, error: AlertTriangle, info: Info };

// Following the system's restraint: the success icon is ink, not green. The
// border and text carry the tone.
const TONES = {
  success: 'text-ink',
  error: 'text-red-600',
  info: 'text-accent',
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
  }, []);

  const push = useCallback(
    (tone, message, options = {}) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      // FE-8: they stack rather than replacing one another.
      setToasts((current) => [...current, { id, tone, message, description: options.description }]);

      const duration = options.duration ?? DURATIONS[tone] ?? 5000;
      if (duration > 0) timers.current.set(id, setTimeout(() => dismiss(id), duration));
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => { pending.forEach(clearTimeout); pending.clear(); };
  }, []);

  /**
   * FE-7: toasts are for the result of an action the user took — saved, copied,
   * revoked, refunded. Never for form validation (inline), never for loading
   * (in place), never for anything the user must act on (a dialog).
   */
  const value = useMemo(
    () => ({
      success: (message, options) => push('success', message, options),
      error: (message, options) => push('error', message, options),
      info: (message, options) => push('info', message, options),
      apiError: (err, fallback = 'That did not work') =>
        push('error', err?.message ?? fallback, {
          description: err?.requestId ? `Request ${err.requestId}` : undefined,
        }),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* FE-8: role="status" so it is announced, and pointer-events-none on the
          container so a toast never blocks the UI beneath it. */}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:bottom-auto sm:right-0 sm:top-0 sm:items-end"
      >
        <AnimatePresence initial={false}>
          {toasts.map((toast) => {
            const Icon = ICONS[toast.tone] ?? Info;
            return (
              <motion.div
                key={toast.id}
                layout
                initial={{ opacity: 0, y: 8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.14 } }}
                transition={{ duration: 0.22, ease: [0.2, 0.7, 0.2, 1] }}
                className="pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-[10px] border px-3.5 py-2.5"
                style={{
                  background: 'rgb(var(--surface))',
                  borderColor: 'rgb(var(--line))',
                  boxShadow: 'var(--shadow-md)',
                }}
              >
                <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${TONES[toast.tone] ?? TONES.info}`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-snug text-ink">{toast.message}</p>
                  {toast.description && (
                    <p className="mt-0.5 text-xs leading-relaxed text-muted">{toast.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(toast.id)}
                  aria-label="Dismiss notification"
                  className="-m-1 shrink-0 rounded p-1 text-faint transition-colors hover:text-ink"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside a ToastProvider');
  return context;
}

export default ToastProvider;
